import { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import axios from "axios";
import { Navigation } from "../components/Navigation";
import { Footer } from "../components/Footer";
import { ProductLightbox } from "../components/ProductLightbox";
import { SearchTrigger } from "../components/GlobalSearch";
import { Maximize2 } from "lucide-react";
import { API, thumbUrl } from "../config";
import { isPapel, papelCardDescription } from "../lib/productDetails";
import { CATEGORIES } from "../data";
import { useLang, productGroupLabel } from "../i18n";
import { setSeo, setProductListJsonLd, clearProductListJsonLd } from "../seo";

const SEO = {
  "kaplama": { title: "Doğal Ahşap Kaplama | İnter Orman Ürünleri", desc: "Doğal ahşap kaplama çeşitleri: ceviz, meşe, tik, okaliptus ve 100'den fazla ithal kaplama. Ahşap kaplama ithalat ve ihracatta 1983'ten beri." },
  "kaplama/papel": { title: "Papel Kaplama | İnter Orman Ürünleri", desc: "Papel ahşap kaplama çeşitleri: teak, ceviz, meşe ve daha fazlası. 1 mm, 1.5 mm, 2 mm, 2.5 mm ve 3 mm kalınlık seçenekleri." },
  "kaplama/kok": { title: "Kök Kaplama | İnter Orman Ürünleri", desc: "Kök kaplama (burl) çeşitleri: ceviz kök, meşe kök, mazel kök, karaağaç kök ve daha fazlası. İthal kök kaplamada geniş stok." },
  "kaplama/kaburga": { title: "Piramit Kaplama | İnter Orman Ürünleri", desc: "Piramit kaplama çeşitleri: ceviz, kiraz, okume, anigre piramit ve daha fazlası. İthal piramit kaplamada geniş stok." },
  "kaplama/piramit": { title: "Piramit Kaplama | İnter Orman Ürünleri", desc: "Piramit kaplama çeşitleri: ceviz, kiraz, okume, anigre piramit ve daha fazlası. İthal piramit kaplamada geniş stok." },
  "kaplama-yapay": { title: "Yapay Kaplama | İnter Orman Ürünleri", desc: "Yapay ahşap kaplama çeşitleri: normal ve 3D yüzey yapay kaplamalar. Tutarlı desen ve renk, projelik geniş stok." },
  "kaplama-yapay/normal": { title: "Normal Yapay Kaplama | İnter Orman Ürünleri", desc: "Normal yapay ahşap kaplama çeşitleri. Tutarlı desen ve renk, projelik geniş stok." },
  "kaplama-yapay/3d": { title: "3D Yüzey Yapay Kaplama | İnter Orman Ürünleri", desc: "3D yüzey yapay ahşap kaplama çeşitleri. Dokulu modern yüzeyler, projelik geniş stok." },
  "boyali-kaplama": { title: "Boyalı Kaplama | İnter Orman Ürünleri", desc: "Boyalı ahşap kaplama çeşitleri: dışbudak, ceviz, koto, poplar, sycamore ve daha fazlası. Açık, orta ve koyu tonlarda geniş stok." },
  "kereste": { title: "Kereste | İnter Orman Ürünleri", desc: "İthal kereste çeşitleri: Afrika, Amerika, Avrupa ve Uzak Doğu kerestelerinde geniş stok." },
  "vinterio": { title: "Vinterio | İnter Orman Ürünleri", desc: "Vinterio serisi ahşap kaplamalar: meşe, kiraz, sapelli, driftwood ve daha fazlası." },
  "linea": { title: "Linea | İnter Orman Ürünleri", desc: "Linea serisi ahşap kaplamalar: meşe, ceviz, larch, birch ve daha fazlası." },
};

export default function CategoryPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, t, dn } = useLang();
  const slug = location.pathname.replace(/^\//, "");
  const KAPLAMA = CATEGORIES.find((c) => c.id === "kaplama");
  const YAPAY = CATEGORIES.find((c) => c.id === "kaplama-yapay");
  const PAGE = {
    "kaplama-yapay": { id: "kaplama-yapay", category: "Yapay Kaplama", title: YAPAY.title, subtitle: YAPAY.subtitle, description: YAPAY.description, image: YAPAY.image },
    "kaplama-yapay/normal": { id: "yapay-normal", category: "Yapay Kaplama", subcategory: "Normal", title: "Normal (Yapay)", subtitle: "Yapay Kaplama", description: "[Normal yapay kaplama ürün grubunuzu buraya yazın.]", image: "https://static.prod-images.emergentagent.com/jobs/1bd54f6f-4f89-4274-9cb9-14f0826e9831/images/4979c6ff562122788f7f9a6d836a8061b854e40ece134025cf9b2f60b7e60a5b.jpeg" },
    "kaplama-yapay/3d": { id: "yapay-3d", category: "Yapay Kaplama", subcategory: "3D Yüzey", title: "3D Yüzey", subtitle: "Yapay Kaplama", description: "[3D yüzey yapay kaplama ürün grubunuzu buraya yazın.]", image: "https://static.prod-images.emergentagent.com/jobs/1bd54f6f-4f89-4274-9cb9-14f0826e9831/images/c9cb3fa1a0e2d0b24ab8699e853065fd97fc210fb4b1c1879ea05cfdd73bd894.jpeg" },
    "kaplama": { id: "kaplama", category: "Kaplama", baseOnly: true, title: "Doğal Kaplama", subtitle: "Doğal Ahşap Kaplama", description: KAPLAMA.description, image: "https://static.prod-images.emergentagent.com/jobs/1bd54f6f-4f89-4274-9cb9-14f0826e9831/images/f3e044845407aebb4bf5364e3238279998f14047f0461a5aa2f660beb3d34cb7.jpeg" },
    "kaplama/kok": { id: "kaplama-kok", category: "Kaplama", subcategory: "Kök Kaplama", title: "Kök Kaplama", subtitle: "Doğal Ahşap Kaplama", description: "[Kök kaplama ürün grubunuzu buraya yazın.]", image: "/api/files/veneer-export/products/5e189587-8766-4206-87ea-8baa760303e4.jpg" },
    "kaplama/kaburga": { id: "kaplama-piramit", category: "Kaplama", subcategory: "Piramit Kaplama", title: "Piramit Kaplama", subtitle: "Doğal Ahşap Kaplama", description: "[Piramit kaplama ürün grubunuzu buraya yazın.]", image: "https://static.prod-images.emergentagent.com/jobs/1bd54f6f-4f89-4274-9cb9-14f0826e9831/images/89262a3dd4120ea918c69b26546f676a7aade4c5f35b79081e6f2d71ce860adf.jpeg" },
    "kaplama/piramit": { id: "kaplama-piramit", category: "Kaplama", subcategory: "Piramit Kaplama", title: "Piramit Kaplama", subtitle: "Doğal Ahşap Kaplama", description: "[Piramit kaplama ürün grubunuzu buraya yazın.]", image: "https://static.prod-images.emergentagent.com/jobs/1bd54f6f-4f89-4274-9cb9-14f0826e9831/images/89262a3dd4120ea918c69b26546f676a7aade4c5f35b79081e6f2d71ce860adf.jpeg" },
    "kaplama/papel": { id: "kaplama-papel", category: "Kaplama", subcategory: "Papel Kaplama", title: "Papel Kaplama", subtitle: "Ahşap Kaplama", image: KAPLAMA.image },
    "linea": { id: "linea", category: "Linea", title: "Linea", subtitle: "Linea Serisi", description: "[Linea ürün grubunuzu buraya yazın.]", heroOverlay: [0.25, 0.55], image: "https://static.prod-images.emergentagent.com/jobs/1bd54f6f-4f89-4274-9cb9-14f0826e9831/images/a12c2bf84d6ae8af9611926f9dabea466e50ba08e3268b047b0195c0612277a5.jpeg" },
    "vinterio": { id: "vinterio", category: "Vinterio", title: "Vinterio", subtitle: "Vinterio Serisi", description: "[Vinterio ürün grubunuzu buraya yazın.]", image: "https://static.prod-images.emergentagent.com/jobs/1bd54f6f-4f89-4274-9cb9-14f0826e9831/images/be5dbfbc38f066b02080428578a1dfcb0cebc2d496a3576ab295e2e79fb2f350.jpeg" },
    "kereste": { id: "kereste", category: "Kereste", subFilters: ["Afrika", "Amerika", "Avrupa", "Uzak Doğu"], filterLabel: "Bölge", title: "Kereste", subtitle: "Ahşap Kereste", description: "[Kereste ürün grubunuzu buraya yazın.]", image: KAPLAMA.image },
    "boyali-kaplama": { id: "boyali-kaplama", category: "Boyalı Kaplama", title: "Boyalı Kaplama", subtitle: "Boyalı Ahşap Kaplama", description: "[Boyalı kaplama ürün grubunuzu buraya yazın.]", image: "https://static.prod-images.emergentagent.com/jobs/1bd54f6f-4f89-4274-9cb9-14f0826e9831/images/de233e4bac11161e0e3fbaa70ab91c038e473621aae8f045a43bd01d8c30d269.jpeg" },
    "kontrplak": { id: "kontrplak", category: "Kontrplak", title: "Kontrplak", subtitle: "Ahşap Kontrplak", description: "[Kontrplak ürün grubunuzu buraya yazın.]", image: "https://images.pexels.com/photos/5089122/pexels-photo-5089122.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  };
  const config = PAGE[slug] || PAGE["kaplama"];
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [activeSub, setActiveSub] = useState("Tümü");
  const [activeTone, setActiveTone] = useState("Tümü");
  const [activeCut, setActiveCut] = useState("Tümü");
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const overlay = useTransform(scrollYProgress, [0, 1], config.heroOverlay || [0.5, 0.85]);

  useEffect(() => {
    const seo = SEO[slug];
    setSeo({
      title: seo?.title || `${config.title} | İnter Orman Ürünleri`,
      description: seo?.desc,
      path: `/${slug}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setActiveSub("Tümü");
    setActiveTone("Tümü");
    setActiveCut("Tümü");
    setLightbox(null);
    window.scrollTo(0, 0);

    const bolge = new URLSearchParams(location.search).get("bolge");
    if (bolge && config.subFilters?.includes(bolge)) setActiveSub(bolge);

    const applyData = (data) => {
      if (cancelled) return;
      const pid = new URLSearchParams(location.search).get("p");
      // Preserve links shared before Papel became a separate collection.
      if (config.baseOnly && pid && data.some((p) => p.id === pid && isPapel(p))) {
        navigate(`/kaplama/papel${location.search}`, { replace: true });
        return;
      }
      const list = data.filter((p) =>
        p.category === config.category &&
        (config.baseOnly ? !p.subcategory : (!config.subcategory || p.subcategory === config.subcategory))
      );
      if (config.category === "Yapay Kaplama") {
        const num = (n) => { const m = /^INT-(\d+)/i.exec(n); return m ? parseInt(m[1], 10) : Infinity; };
        list.sort((a, b) => num(a.name) - num(b.name));
      }
      setProducts(list);
      setProductListJsonLd({
        name: SEO[slug]?.title || config.title,
        path: `/${slug}`,
        products: list,
      });
      if (pid) {
        const idx = list.findIndex((x) => x.id === pid);
        if (idx >= 0) setLightbox(idx);
      }
      setLoading(false);
    };

    const fetchProducts = (attempt = 0) => {
      axios
        .get(`${API}/products`, { timeout: 20000 })
        .then((res) => applyData(res.data))
        .catch(() => {
          if (cancelled) return;
          if (attempt < 3) setTimeout(() => fetchProducts(attempt + 1), 1200);
          else setLoading(false);
        });
    };

    fetchProducts();
    return () => { cancelled = true; clearProductListJsonLd(); };
  }, [slug, location.search]);

  // Alt kategoriler ürün verisinden dinamik türetilir (sadece kilitli olmayan sayfalarda)
  const subcategories = useMemo(() => {
    if (config.subcategory) return [];
    if (config.subFilters) return ["Tümü", ...config.subFilters];
    const set = [...new Set(products.map((p) => p.subcategory).filter(Boolean))];
    return set.length > 0 ? ["Tümü", ...set] : [];
  }, [products, config.subcategory, config.subFilters]);

  const filtered = useMemo(() => {
    return products.filter(
      (p) =>
        (activeSub === "Tümü" || p.subcategory === activeSub) &&
        (activeTone === "Tümü" || p.tone === activeTone) &&
        (activeCut === "Tümü" || p.cut === activeCut)
    );
  }, [products, activeSub, activeTone, activeCut]);

  // Papel now has its own collection; natural veneer keeps the existing cuts.
  const cuts = useMemo(() => {
    if (config.id !== "kaplama") return [];
    const present = new Set(products.map((p) => p.cut).filter(Boolean));
    if (present.size === 0) return [];
    const order = ["Hareli", "Freze", "Pomela"];
    return ["Tümü", ...order.filter((c) => present.has(c))];
  }, [products, config.id]);

  // Renk tonu filtresi: sadece Boyalı Kaplama'da göster
  const tones = useMemo(() => {
    if (config.category !== "Boyalı Kaplama") return [];
    const present = new Set(products.map((p) => p.tone).filter(Boolean));
    if (present.size === 0) return [];
    const order = ["Açık", "Orta", "Koyu"];
    return ["Tümü", ...order.filter((t) => present.has(t))];
  }, [products, config.category]);

  return (
    <div data-testid={`category-page-${config.id}`} className="bg-white">
      <Navigation />

      {/* Hero header */}
      <section ref={ref} className="relative h-[42vh] min-h-[340px] overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-x-0 -inset-y-[5%] will-change-transform">
          <img src={config.image} alt={config.title} fetchPriority="high" className="w-full h-full object-cover" />
        </motion.div>
        <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-[#0D0C0B]" />
        <div className={`absolute inset-0 bg-gradient-to-t ${config.heroOverlay ? "from-[#0D0C0B] via-transparent to-transparent" : "from-[#0D0C0B] via-transparent to-[#0D0C0B]/50"}`} />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-sans uppercase tracking-[0.3em] text-xs text-[#7BA05B] mb-5"
          >
            {dn(config.subtitle)}
          </motion.p>
          <motion.h1
            data-testid="category-heading"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-5xl md:text-7xl tracking-tighter text-[#F2EFEB] leading-none"
          >
            {dn(config.title)}
          </motion.h1>
        </div>
      </section>

      {/* Products — beyaz zemin */}
      <div className="bg-white">
      <section className="pt-6 md:pt-8 pb-20 md:pb-28 max-w-7xl mx-auto px-6 md:px-12">
        <SearchTrigger light className="mb-14" />
        <div className="flex items-baseline gap-3 mb-10">
          <h2 className="font-serif text-3xl md:text-4xl text-[#1A1715]">{t("category.products")}</h2>
          <span data-testid="category-product-count" className="text-[#6B6560]">({filtered.length})</span>
        </div>

        {/* Subcategory filter chips */}
        {!loading && subcategories.length > 0 && (
          <div data-testid="subcategory-filters" className="flex flex-wrap items-center gap-2 mb-12">
            {config.filterLabel && <span className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#8C8681] mr-2">{dn(config.filterLabel)}</span>}
            {subcategories.map((sub) => (
              <button
                key={sub}
                data-testid={`filter-chip-${sub === "Tümü" ? "all" : sub}`}
                onClick={() => setActiveSub(sub)}
                className={`px-5 py-2 rounded-full text-xs font-sans uppercase tracking-[0.15em] border transition-all duration-300 ${
                  activeSub === sub
                    ? "bg-[#7BA05B] border-[#7BA05B] text-[#0D0C0B]"
                    : "border-black/15 text-[#6B6560] hover:text-[#1A1715] hover:border-black/40"
                }`}
              >
                {dn(sub)}
              </button>
            ))}
          </div>
        )}

        {/* Doğal Kaplama: Hareli / Freze / Pomela */}
        {!loading && cuts.length > 0 && (
          <div data-testid="cut-filters" className="flex flex-wrap items-center gap-2 mb-12">
            <span className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#8C8681] mr-2">{t("category.cut")}</span>
            {cuts.map((c) => (
              <button
                key={c}
                data-testid={`cut-chip-${c === "Tümü" ? "all" : c}`}
                aria-pressed={activeCut === c}
                onClick={() => setActiveCut(c)}
                className={`px-5 py-2 rounded-full text-xs font-sans uppercase tracking-[0.15em] border transition-all duration-300 ${
                  activeCut === c
                    ? "bg-[#2F4A26] border-[#2F4A26] text-white"
                    : "border-black/15 text-[#6B6560] hover:text-[#1A1715] hover:border-black/40"
                }`}
              >
                {dn(c)}
              </button>
            ))}
          </div>
        )}

        {/* Renk tonu filter chips (Boyalı Kaplama vb.) */}
        {!loading && tones.length > 0 && (
          <div data-testid="tone-filters" className="flex flex-wrap items-center gap-2 mb-12">
            <span className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#8C8681] mr-2">{t("category.tone")}</span>
            {tones.map((t) => (
              <button
                key={t}
                data-testid={`tone-chip-${t === "Tümü" ? "all" : t}`}
                onClick={() => setActiveTone(t)}
                className={`px-5 py-2 rounded-full text-xs font-sans uppercase tracking-[0.15em] border transition-all duration-300 ${
                  activeTone === t
                    ? "bg-[#C67D57] border-[#C67D57] text-[#0D0C0B]"
                    : "border-black/15 text-[#6B6560] hover:text-[#1A1715] hover:border-black/40"
                }`}
              >
                {dn(t)}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-[#6B6560] font-sans uppercase tracking-[0.2em] text-xs">{t("common.loading")}</div>
        ) : products.length === 0 ? (
          <div data-testid="category-empty" className="text-[#6B6560] py-20 text-center border border-black/10 rounded-lg">
            {t("category.empty")}
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="category-no-results" className="text-[#6B6560] py-20 text-center border border-black/10 rounded-lg">
            {t("category.noResults")}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {filtered.map((p, i) => (
              <motion.div
                key={p.id}
                data-testid={`category-product-${p.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "60px" }}
                transition={{ duration: 0.45, delay: (i % 4) * 0.04 }}
                className="cv-card group cursor-pointer"
                onClick={() => setLightbox(i)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setLightbox(i); } }}
              >
                <div className="relative overflow-hidden aspect-[4/5] mb-3 bg-[#F0EDE8]">
                  {p.image ? (
                    <img
                      src={thumbUrl(p.image)}
                      alt={p.name}
                      loading={i < 8 ? "eager" : "lazy"}
                      fetchPriority={i < 8 ? "high" : "auto"}
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#8C8681] text-xs">{t("common.noImage")}</div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D0C0B]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  {/* zoom hint */}
                  <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#7BA05B] text-[#0D0C0B] flex items-center justify-center opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                    <Maximize2 size={16} strokeWidth={2} />
                  </div>
                </div>
                <span className="font-sans uppercase tracking-[0.2em] text-[9px] md:text-[10px] text-[#C67D57]">{productGroupLabel(p, dn)}</span>
                <h3 translate="no" className="notranslate font-serif text-lg md:text-xl text-[#1A1715] mt-0.5 group-hover:text-[#5F8547] transition-colors duration-300">{p.name}</h3>
                {papelCardDescription(p, lang) && (
                  <p data-testid={`category-product-description-${p.id}`} className="mt-2 text-xs md:text-sm leading-relaxed text-[#6B6560] break-words">
                    {papelCardDescription(p, lang)}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </section>
      </div>

      <ProductLightbox
        products={filtered}
        index={lightbox}
        onClose={() => setLightbox(null)}
        onNavigate={setLightbox}
      />

      <Footer />
    </div>
  );
}
